import React, { useContext, useMemo } from 'react';
import {
  MetricStatus,
  BillingMetrics,
} from '../../../shared/BillingMetrics';

import DauConsumption from './components/DauConsumption';
import DeuConsumption from './components/DeuConsumption';
import MiuConsumption from './components/MiuConsumption';
import MeuConsumption from './components/MeuConsumption';
import AgentConsumption from './components/AgentConsumption';
import SessionConsumption from './components/SessionConsumption';
import AdvancedDailyEngagedUserConsumption from './components/AdvancedDailyEngagedUserConsumption';
import AiTokenConsumption from './components/AiTokenConsumption';

import DataConsumptionContext from '../../../contexts/Billing/DataConsumptionContext';
import { useDauConsumptionContext } from '../../../contexts/Billing/DauConsumptionContext';
import { useDeuConsumptionContext } from '../../../contexts/Billing/DeuConsumptionContext';
import { useBillingMetricsContext } from '../../../contexts/Billing/BillingMetricsContext';
import { useMiuConsumptionContext } from '../../../contexts/Billing/MiuConsumptionContext';
import { useMeuConsumptionContext } from '../../../contexts/Billing/MeuConsumptionContext';
import { useAgentConsumptionContext } from '../../../contexts/Billing/AgentConsumptionContext';
import DataConsumptionMetricContext from '../../../contexts/Billing/DataConsumptionMetricContext';
import { useSessionConsumptionContext } from '../../../contexts/Billing/SessionConsumptionContext';
import { useAdvancedDeuConsumptionContext } from '../../../contexts/Billing/AdvancedDeuConsumptionContext';
import { useAiTokenConsumptionDataContext } from '../../../contexts/Billing/AiTokenConsumptionDataContext';
import FeatureToggleService from '../../../services/FeatureToggleService';
import { ADVANCED_DEU_ENABLED, AI_TOKEN_ENABLED } from '../../../shared/FeatureToggleKeys';

const DataConsumptionComponent: React.FC = () => {
  const { isMetricAllowed } = useContext(DataConsumptionContext);

  const { hasDuplicatedExchangedMessage } = useBillingMetricsContext();

  const [showAiTokenSection, setShowAiTokenSection] = React.useState(false);
  React.useEffect(() => {
    const checkAiTokenFeatureToggles = async () => {
      const [advancedDeuEnabled, aiTokenEnabled] = await Promise.all([
        FeatureToggleService.isFeatureToggleEnabledByCluster(ADVANCED_DEU_ENABLED),
        FeatureToggleService.isFeatureToggleEnabled(AI_TOKEN_ENABLED),
      ]);
      setShowAiTokenSection(advancedDeuEnabled && aiTokenEnabled);
    };
    checkAiTokenFeatureToggles();
  }, []);

  const {
    compiledDailyActiveUserDataState,
    consolidatedDauApiRequisition,
    isDauLoading,
  } = useDauConsumptionContext();

  const {
    compiledDailyEngagedUserDataState,
    consolidatedDeuApiRequisition,
    isDeuLoading,
  } = useDeuConsumptionContext();

  const {
    compiledAdvancedDailyEngagedUserDataState,
    consolidatedAdvancedDeuApiRequisition,
    isAdvancedDeuLoading,
    advancedDeuMetricIds,
    isDeuAiEnabled,
  } = useAdvancedDeuConsumptionContext();

  const {
    compiledAgentConsumptionDataState,
    consolidatedAgentApiRequisition,
    isAgentLoading,
  } = useAgentConsumptionContext();

  const {
    compiledSessionConsumptionDataState,
    consolidatedSessionApiRequisition,
    isSessionLoading,
  } = useSessionConsumptionContext();

  const {
    compiledMiuConsumptionDataState,
    consolidatedMiuApiRequisition,
    isMiuLoading,
    miuStatus,
    miuMetricIds,
    localization,
  } = useMiuConsumptionContext();

  const {
    compiledMeuConsumptionDataState,
    consolidatedMeuApiRequisition,
    isMeuLoading,
    meuStatus,
    meuMetricIds,
    localizationMeu,
  } = useMeuConsumptionContext();

  const {
    totalAiTokenConsumption,
    dailyAiTokenConsumption,
    containsDataOnSelectedMonth: containsAiTokenDataOnSelectedMonth,
    isAiTokenLoading,
    consolidatedAiTokenApiRequisition,
  } = useAiTokenConsumptionDataContext();

  const contextProps = useMemo(() => ({
    consolidatedDauApiRequisition,
    compiledDailyActiveUserDataState,
    isDauLoading,

    consolidatedDeuApiRequisition,
    compiledDailyEngagedUserDataState,
    isDeuLoading,

    consolidatedAdvancedDeuApiRequisition,
    compiledAdvancedDailyEngagedUserDataState,
    isAdvancedDeuLoading,
    advancedDeuMetricIds,
    isDeuAiEnabled,

    consolidatedAgentApiRequisition,
    compiledAgentConsumptionDataState,
    isAgentLoading,

    consolidatedSessionApiRequisition,
    compiledSessionConsumptionDataState,
    isSessionLoading,

    consolidatedMiuApiRequisition,
    compiledMiuConsumptionDataState,
    isMiuLoading,
    miuMetricIds,
    localization,

    consolidatedMeuApiRequisition,
    compiledMeuConsumptionDataState,
    isMeuLoading,
    meuMetricIds,
    localizationMeu,

    totalAiTokenConsumption,
    dailyAiTokenConsumption,
    containsDataOnSelectedMonth: containsAiTokenDataOnSelectedMonth,
    isAiTokenLoading,
    consolidatedAiTokenApiRequisition,
  }), [
    consolidatedDauApiRequisition,
    compiledDailyActiveUserDataState,
    isDauLoading,

    consolidatedDeuApiRequisition,
    compiledDailyEngagedUserDataState,
    isDeuLoading,

    consolidatedAdvancedDeuApiRequisition,
    compiledAdvancedDailyEngagedUserDataState,
    isAdvancedDeuLoading,
    advancedDeuMetricIds,
    isDeuAiEnabled,

    consolidatedAgentApiRequisition,
    compiledAgentConsumptionDataState,
    isAgentLoading,

    consolidatedSessionApiRequisition,
    compiledSessionConsumptionDataState,
    isSessionLoading,

    consolidatedMiuApiRequisition,
    compiledMiuConsumptionDataState,
    isMiuLoading,
    miuMetricIds,
    localization,

    consolidatedMeuApiRequisition,
    compiledMeuConsumptionDataState,
    isMeuLoading,
    meuMetricIds,
    localizationMeu,

    totalAiTokenConsumption,
    dailyAiTokenConsumption,
    containsAiTokenDataOnSelectedMonth,
    isAiTokenLoading,
    consolidatedAiTokenApiRequisition,
  ]);

  const haveMiuMSG = miuMetricIds.includes(BillingMetrics.exchangedMessage);
  const existMiu = miuMetricIds.includes(BillingMetrics.monthlyActiveUser);
  const errorMiu = miuStatus === MetricStatus.error;

  const haveMeuMSG = meuMetricIds.includes(BillingMetrics.exchangedMessage);
  const existMeu = meuMetricIds.includes(BillingMetrics.monthlyEngagedUser);
  const errorMeu = meuStatus === MetricStatus.error;

  const showSingleMsg = hasDuplicatedExchangedMessage?.(miuMetricIds, meuMetricIds);

  const showMiu = (((haveMiuMSG && miuMetricIds.length > 1) || existMiu) && !errorMiu);
  const showMeu = (((haveMeuMSG && meuMetricIds.length > 1) || existMeu) && !errorMeu);

  const hasAdvancedDeuMetrics =
    isMetricAllowed(BillingMetrics.userConversation) &&
    isMetricAllowed(BillingMetrics.prospectConversation) &&
    isMetricAllowed(BillingMetrics.activeMessage) &&
    isMetricAllowed(BillingMetrics.dailyEngagedUser);
  
  return (
    <DataConsumptionMetricContext.Provider value={contextProps}>
      {hasAdvancedDeuMetrics && <AdvancedDailyEngagedUserConsumption />}
      {showAiTokenSection && isMetricAllowed(BillingMetrics.aiToken) && <AiTokenConsumption />}
      {isMetricAllowed(BillingMetrics.dailyActiveUser) && <DauConsumption />}
      {isMetricAllowed(BillingMetrics.dailyEngagedUser) && !hasAdvancedDeuMetrics && <DeuConsumption />}
      {isMetricAllowed(BillingMetrics.agent) && <AgentConsumption />}

      {(() => {
        if (showSingleMsg) {
          return haveMiuMSG && !errorMiu ? <MiuConsumption /> : null;
        } else {
          return (
            <>
              {showMiu && <MiuConsumption />}
              {showMeu && <MeuConsumption />}
            </>
          );
        }
      })()}
      {isMetricAllowed(BillingMetrics.session) && <SessionConsumption />}

    </DataConsumptionMetricContext.Provider>
  );
};

export default DataConsumptionComponent;
